import useFetch from "common/hooks/useFetch";
import React, { useEffect, useState } from "react";
import Icon from "components/Icon/Icon";
import { parseStrapiFormat } from "../../utils/strapi";
import Select, { Props as SelectProps } from "../Select/Select";
import { t } from "i18next";

const postExecute = (response: any) => {
  return parseStrapiFormat(response);
};

export type RemoteSelectProps = {
  onDataLoaded?: (value: any) => void;
  url?: string;
  filters?: any;
  sorts?: string[];
  populate?: any;
  processOptions?: (value: any) => void;
  additionalOptions?: any;
  multiple?: boolean;
  alsoOnChange?: () => void;
  filtersComponents: {
    name: string;
    key: string;
    component: React.ReactNode;
  }[];
} & SelectProps;

export function RemoteSelectWithFilters({
  onDataLoaded = () => {},
  url,
  filters = [],
  sorts = [],
  populate = [],
  processOptions = () => {},
  additionalOptions = () => {},
  multiple = false,
  alsoOnChange,
  filtersComponents = [],
  ...rest
}: RemoteSelectProps) {
  const [options, setOptions] = useState<any>([]);
  const [params, setParams] = useState<any>({});

  let paramsUpdated = false;
  const newParams: any = {};

  filters.forEach((f: any) => {
    newParams[`filters[${f.name}]`] = f.value;
    if (newParams[`filters[${f.name}]`] !== params[`filters[${f.name}]`]) {
      paramsUpdated = true;
    }
  });

  populate.forEach((p: any, index: number) => {
    newParams[`populate[${index}]`] = p;
    if (newParams[`populate[${index}]`] !== params[`populate[${index}]`]) {
      paramsUpdated = true;
    }
  });

  sorts?.forEach((s: any) => {
    newParams.sort = s;
    if (newParams.sort !== params.sort) {
      paramsUpdated = true;
    }
  });

  if (paramsUpdated) {
    setParams(newParams);
  }

  const getUrl = () => {
    const esc = encodeURIComponent;
    const fullparams = { ...params };
    fullparams[`pagination[limit]`] = 5000;
    const queryParams = Object.keys(fullparams)
      .filter((key) => fullparams[key] != null)
      .map((key) => `${esc(key)}=${esc(fullparams[key])}`)
      .join("&");

    return `${url}${queryParams ? "?" : ""}${queryParams}`;
  };

  const { data, isFetching }: any = useFetch(getUrl(), {
    postExecute,
    sharePromise: true,
  });

  useEffect(() => {
    let newData = data || [];

    if (onDataLoaded) {
      onDataLoaded(newData);
    }

    if (additionalOptions.length && Array.isArray(newData)) {
      newData = [...additionalOptions, ...newData];
    }

    const opts = processOptions ? processOptions(newData) : newData;

    if (JSON.stringify(options) !== JSON.stringify(opts)) {
      setOptions(opts);
    }
  }, [
    additionalOptions,
    data,
    onDataLoaded,
    options,
    processOptions,
    setOptions,
  ]);

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const updateFilter = (key: string, value: any) => {
    setParams((prevParams: any) => ({
      ...prevParams,
      [`filters[${key}]`]: value,
    }));
  };

  return (
    <div>
      <div className="w-full rounded-lg mb-2 -mt-8 z-[100]">
        <div
          onClick={toggleExpand}
          className="w-full  py-2 text-right cursor-pointer rounded-t-lg flex justify-end"
        >
          <span>
            {isExpanded ? t("filters.hideFilters") : t("filters.showFilters")}
          </span>
          <Icon
            className="text-gray-500 w-5 ml-2"
            name={isExpanded ? "ChevronUp" : "ChevronDown"}
          />
        </div>
        {isExpanded && (
          <div className="w-full flex flex-wrap gap-2 mb-4">
            {filtersComponents.map((filter) => (
              <div
                key={`${filter.key}`}
                className="grid grid-cols-3 w-full items-center gap-6"
              >
                <span className="text-black font-bold">{filter.name}</span>
                {React.cloneElement(filter.component as React.ReactElement, {
                  onChange: (value: any) => updateFilter(filter.key, value),
                  value: params[`filters[${filter.key}]`] || "",
                  className: "col-span-2",
                })}
              </div>
            ))}
            <div className="w-full h-0.5 border bg-gray-200" />
          </div>
        )}
      </div>
      <Select
        options={options}
        multiple={multiple}
        isLoading={isFetching}
        alsoOnChange={alsoOnChange}
        {...rest}
      />
    </div>
  );
}

export default RemoteSelectWithFilters;
